import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AccordionModule } from 'primeng/accordion';
import { Avatar } from 'primeng/avatar';
import { DatePicker } from 'primeng/datepicker';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { RequisicionesCandidatoService } from 'src/app/apps/nodo/modules/seleccion/requisiciones/services/requisiciones-candidato.service';
import { MaterialModule } from 'src/app/material.module';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { PersonService } from '../../services/person.service';
import { IDropdown } from 'src/app/apps/nodo/modules/administracion/interfaces/IDropdown';
import { catchError, finalize, forkJoin, of, throwError } from 'rxjs';
import { IPersonDataBasicAditionalDto } from '../../interfaces/IPersonDataBasicAditionalDto';
import { DireccionDianDirective } from 'src/app/shared/directives/direccion-dian.directive';

@Component({
    selector: 'app-datos-basicos-adicionales',
    imports: [DireccionDianDirective, 
        ReactiveFormsModule,
        InputTextModule,
        StackDirectives,
        Select,
        // DatePicker,
        InputMaskModule,
        AccordionModule,
        TextareaModule,
        MaterialModule,
        CommonModule
    ],
    templateUrl: './datos-basicos-adicionales.html',
    styleUrl: './datos-basicos-adicionales.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatosBasicosAdicionales {
    private cdr = inject(ChangeDetectorRef);
    private loader = inject(LoaderService);
    private masterService = inject(MasterService);
    private ac = inject(ActivatedRoute);
    private requisitionCandidateService = inject(RequisicionesCandidatoService);
    private toastr = inject(ToastrService);
    private fb = inject(FormBuilder);
    private personService = inject(PersonService);
    private router = inject(Router);

    idEncrypt: any;
    @Output() validChange = new EventEmitter<boolean>();

    countryList: IDropdown[] = [];
    citiesList: IDropdown[] = [];
    civilStatusList: IDropdown[] = [];
    departmentCitiesResList: IDropdown[] = [];
    tipoViviendaList: IDropdown[] = [];
    departmentCitiesNacList: IDropdown[] = [];
    parentescoList: IDropdown[] = [];
    epsList: IDropdown[] = [];
    pensionList: IDropdown[] = [];
    tallaCamisaList: IDropdown[] = [];
    tallaPantalonList: IDropdown[] = [];
    tallaZapatoList: IDropdown[] = [];
    nivelSocioEcoList: IDropdown[] = [];

    form = this.fb.group({
        //datos de residencia
        paiRes: ['', Validators.required],
        // depRes: ['', Validators.required],
        ciuRes: ['', Validators.required],
        barrio: ['', [Validators.required, Validators.maxLength(50)]],
        tipoVivienda: ['', Validators.required],
        direccion: ['', [Validators.required, Validators.maxLength(100)]],
        direccionContactoEmergencia: ['', [Validators.required, Validators.maxLength(100)]],
        // datos adicionales
        nombreContacto: ['', Validators.required],
        telefonoContacto: ['', Validators.required],
        ingTrabajo: [true],
        ingOtros: [false],
        ingObs: [''],
        parentesco: ['', Validators.required],
        eps: ['', Validators.required],
        pension: ['', Validators.required],
        tallaCamisa: ['', Validators.required],
        tallaPantalon: ['', Validators.required],
        tallaZapato: ['', Validators.required],
        estatura: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
        peso: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
        idNivelSocioEconomico: ['', Validators.required]
    });

    ngOnInit() {
        this.ac.queryParams.subscribe((params) => {
            const rawId = params['q'];
            if (rawId) {
                this.idEncrypt = encodeURIComponent(rawId);
                this.loadData();
            } else {
                this.router.navigate(['/login']);
            }

            this.form.statusChanges.subscribe((status) => {
                this.validChange.emit(status === 'VALID');
            });

            this.form.get('ingOtros')?.valueChanges.subscribe((isChecked: boolean | null) => {
                const ingObsControl = this.form.get('ingObs');
                if (!ingObsControl) return;

                if (isChecked) {
                    // Habilita y vuelve obligatorio
                    ingObsControl.enable({ emitEvent: false });
                    ingObsControl.setValidators([Validators.required]);
                } else {
                    // Quita obligatoriedad, deshabilita y limpia
                    ingObsControl.clearValidators();
                    ingObsControl.reset(); // limpia valor y estado
                    ingObsControl.disable({ emitEvent: false });
                }

                // Recalcula validez para que el UI y el stepper reaccionen
                ingObsControl.updateValueAndValidity({ emitEvent: false });
            });
        });

        this.cdr.markForCheck();
        this.form.markAllAsTouched();
    }

    loadData() {
        forkJoin({
            countries: this.masterService
                .getCountries()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            civilStatus: this.masterService
                .getCivilStatus()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            bloodGroups: this.masterService
                .getBloodGroups()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            gender: this.masterService
                .getGendersInfoCandidate()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            housingType: this.masterService
                .getHousingTypes()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            person: this.requisitionCandidateService.getInfoCandidateById(this.idEncrypt),
            relationship: this.masterService
                .getRelationship()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            tallaCamisa: of(this.getTallaCamisa()),
            tallaPantalon: of(this.getTallaPantalon()),
            tallaZapato: of(this.getTallaZapato()),
            pension: this.masterService
                .getPension()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            health: this.masterService
                .getHealth()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
            nivelEconomico: this.masterService
                .getEconomicLevels()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err))))
        })
            .pipe(finalize(() => this.loader.hide()))
            .subscribe({
                next: ({
                    countries,
                    civilStatus,
                    bloodGroups,
                    gender,
                    housingType,
                    person,
                    relationship,
                    tallaCamisa,
                    tallaPantalon,
                    tallaZapato,
                    pension,
                    health,
                    nivelEconomico
                }) => {
                    const dataPerson = person.data.candidateInfo;

                    this.countryList = (countries?.data ?? []).map((item: any) => ({
                        id: item.cod_pai,
                        label: item.nom_pai
                    }));
                    this.civilStatusList = civilStatus.data;
                    this.tipoViviendaList = housingType.data;
                    this.parentescoList = relationship.data;
                    this.tallaCamisaList = tallaCamisa;
                    this.tallaPantalonList = tallaPantalon;
                    this.tallaZapatoList = tallaZapato;
                    this.pensionList = pension.data;
                    this.epsList = health.data;
                    this.nivelSocioEcoList = nivelEconomico.data;

                    // Cargar datos del candidato
                    this.form.patchValue({
                        // datos de residencia
                        paiRes: dataPerson.idPaisResidencia,
                        // depRes: dataPerson.idDepResidencia,
                        ciuRes:
                            dataPerson?.idCiuResidencia && dataPerson?.idDepResidencia
                                ? `${dataPerson.idCiuResidencia} - ${dataPerson.idDepResidencia}`
                                : null,
                        barrio: dataPerson.barrio,
                        tipoVivienda: dataPerson.idTipoVivienda?.toString(),
                        direccion: dataPerson.direccion,
                        // contacto de emergencia
                        nombreContacto: dataPerson.nombresContactoEmergencia,
                        telefonoContacto: dataPerson.telefonosContactoEmergencia,
                        direccionContactoEmergencia: dataPerson.direccionContactoEmergencia,
                        parentesco: dataPerson.idParentesco?.toString(),
                        ingTrabajo: dataPerson.deDondeObtieneIngresos?.includes('Trabajo') ?? false,
                        ingOtros: dataPerson.deDondeObtieneIngresos?.includes('otros') ?? false,
                        ingObs: dataPerson.detalleIngreso ?? '',
                        eps: dataPerson.idEps?.toString(),
                        pension: dataPerson.idPension?.toString(),
                        tallaCamisa: dataPerson.tallaCamisa ?? '',
                        tallaPantalon: dataPerson.tallaPantalon ?? '',
                        tallaZapato: dataPerson.tallaZapato ?? '',
                        estatura: dataPerson.estatura ?? '',
                        peso: dataPerson.peso ?? '',
                        idNivelSocioEconomico: dataPerson.idNivelSocioEconomico?.toString() ?? ''
                    });

                    if (dataPerson.idPaisResidencia) {
                        this.onCountryResChange({ value: dataPerson.idPaisResidencia });
                    }

                    this.cdr.markForCheck();
                },
                error: (e) => {}
            });
    }

    onCountryResChange(codPai: any) {
        this.getDepartmentsAndCities(codPai.value, (data) => (this.departmentCitiesResList = data));
    }

    getDepartmentsAndCities(cod_pai: string, assignCallback: (data: any[]) => void) {
        this.loader.show();
        this.masterService
            .getDepartmentsCities(cod_pai)
            .pipe(finalize(() => this.loader.hide()))
            .subscribe({
                next: (response) => {
                    assignCallback(response.data);
                    this.cdr.markForCheck();
                },
                error: (e) => {
                    this.toastr.error('Error al cargar');
                }
            });
    }



    saveBasicDataAditional() {
        this.loader.show('Guardando datos...');
        if (this.form.valid && this.validacionAdicional()) {
            const payload = this.setBasicData();
            const form = this.setToFormData(payload);
            this.personService.updateBasicDataAditional(form).subscribe({
                next: () => {
                    this.loader.hide();
                    this.toastr.success('Datos guardados correctamente');
                    // this.stepper.next();
                },
                error: (err) => {
                    this.toastr.error('Error al guardar los datos básicos');
                    this.loader.hide();
                }
            });
        } else {
            this.form.markAllAsTouched(); // Para mostrar errores
        }
    }

    validacionAdicional(): boolean {
        if (this.form.value && this.form.value.direccion && this.form.value.direccion.length > 100) {
            this.toastr.error('La dirección no puede tener más de 100 caracteres');
            return false;
        }
        if (this.form.value && this.form.value.direccionContactoEmergencia && this.form.value.direccionContactoEmergencia.length > 100) {
            this.toastr.error('La dirección del contacto de emergencia no puede tener más de 100 caracteres');
            return false;
        }

        if (this.form.value && this.form.value.barrio && this.form.value.barrio.length > 50) {
            this.toastr.error('El barrio no puede tener más de 50 caracteres');
            return false;
        }
        return true;
    }

    setBasicData(): IPersonDataBasicAditionalDto {
        const dataDepCityRes = this.form.value.ciuRes!.split('-');
        return {
            idEncrypted: this.idEncrypt,
            idPaisResidencia: this.form.value.paiRes!,
            idDepResidencia: dataDepCityRes[1].trim(),
            idCiuResidencia: dataDepCityRes[0].trim(),
            barrio: this.form.value.barrio!,
            idTipoVivienda: Number.parseInt(this.form.value.tipoVivienda!),
            direccion: this.form.value.direccion!,
            direccionContactoEmergencia: this.form.value.direccionContactoEmergencia!,
            nombresContactoEmergencia: this.form.value.nombreContacto!,
            telefonosContactoEmergencia: this.form.value.telefonoContacto!,
            deDondeObtieneIngresos: this.detailIngresos(),
            detalleIngreso: this.form.value.ingObs || '',
            idParentesco: Number.parseInt(this.form.value.parentesco!),
            idEps: Number.parseInt(this.form.value.eps!),
            idPension: Number.parseInt(this.form.value.pension!),
            tallaCamisa: this.form.value.tallaCamisa!,
            tallaPantalon: this.form.value.tallaPantalon!,
            tallaZapato: this.form.value.tallaZapato!,
            estatura: this.form.value.estatura!,
            peso: this.form.value.peso!,
            idNivelSocioEconomico: Number.parseInt(this.form.value.idNivelSocioEconomico!)
        };
    }

    setToFormData(payload: IPersonDataBasicAditionalDto): FormData {
        const form = new FormData();

        // Helper para agregar si existe
        const appendIf = (key: string, value: any) => {
            if (value !== undefined && value !== null) {
                form.append(key, String(value));
            }
        };

        // Mapea exactamente los nombres del DTO del backend:
        appendIf('idEncrypted', payload.idEncrypted);
        appendIf('idPaisResidencia', payload.idPaisResidencia);
        appendIf('idDepResidencia', payload.idDepResidencia);
        appendIf('idCiuResidencia', payload.idCiuResidencia);
        appendIf('barrio', payload.barrio);
        appendIf('idTipoVivienda', payload.idTipoVivienda);
        appendIf('direccion', payload.direccion);
        appendIf('nombresContactoEmergencia', payload.nombresContactoEmergencia);
        appendIf('telefonosContactoEmergencia', payload.telefonosContactoEmergencia);
        appendIf('deDondeObtieneIngresos', payload.deDondeObtieneIngresos);
        appendIf('detalleIngreso', payload.detalleIngreso);
        appendIf('idParentesco', payload.idParentesco);
        appendIf('idEps', payload.idEps);
        appendIf('idPension', payload.idPension);
        appendIf('tallaCamisa', payload.tallaCamisa);
        appendIf('tallaPantalon', payload.tallaPantalon);
        appendIf('tallaZapato', payload.tallaZapato);
        appendIf('estatura', payload.estatura);
        appendIf('peso', payload.peso);
        appendIf('idNivelSocioEconomico', payload.idNivelSocioEconomico);
        appendIf('direccionContactoEmergencia', payload.direccionContactoEmergencia);

        return form;
    }

    detailIngresos(): string {
        return this.form.value.ingTrabajo && this.form.value.ingOtros
            ? 'Trabajo o salario, otros'
            : this.form.value.ingTrabajo
              ? 'Trabajo'
              : this.form.value.ingOtros
                ? 'Otros'
                : 'Ninguno';
    }

    getErrorMessage(controlName: string, errorMessage: string): string | null {
        const control = this.form.get(controlName);
        if (control?.touched) {
            if (control.hasError('required')) {
                return errorMessage ?? 'Este campo es requerido';
            }
            if (control.hasError('pattern')) {
                return 'Solo se permiten números';
            }
            if (control.hasError('minlength')) {
                const minLength = control.getError('minlength').requiredLength;
                return `Mínimo ${minLength} caracteres`;
            }
            if (control.hasError('email')) {
                return 'Correo electrónico inválido';
            }
            if (control.hasError('menorDe16')) {
                return 'Debe ser mayor de 16 años';
            }
        }
        return null;
    }

    getTallaCamisa(): IDropdown[] {
        return [
            { id: 'XS', label: 'XS' },
            { id: 'S', label: 'S' },
            { id: 'M', label: 'M' },
            { id: 'L', label: 'L' },
            { id: 'XL', label: 'XL' },
            { id: 'XXL', label: 'XXL' },
            { id: 'XXXL', label: 'XXXL' }
        ];
    }

    getTallaPantalon(): IDropdown[] {
        return [
            { id: '2', label: '2' },
            { id: '4', label: '4' },
            { id: '6', label: '6' },
            { id: '8', label: '8' },
            { id: '10', label: '10' },
            { id: '12', label: '12' },
            { id: '14', label: '14' },
            { id: '16', label: '16' },
            { id: '18', label: '18' },
            { id: '28', label: '28' },
            { id: '30', label: '30' },
            { id: '32', label: '32' },
            { id: '34', label: '34' },
            { id: '36', label: '36' },
            { id: '38', label: '38' },
            { id: '40', label: '40' },
            { id: '42', label: '42' }
        ];
    }

    getTallaZapato(): IDropdown[] {
        return [
            { id: '30', label: '30' },
            { id: '31', label: '31' },
            { id: '32', label: '32' },
            { id: '33', label: '33' },
            { id: '34', label: '34' },
            { id: '35', label: '35' },
            { id: '36', label: '36' },
            { id: '37', label: '37' },
            { id: '38', label: '38' },
            { id: '39', label: '39' },
            { id: '40', label: '40' },
            { id: '41', label: '41' },
            { id: '42', label: '42' },
            { id: '43', label: '43' },
            { id: '44', label: '44' },
            { id: '45', label: '45' }
        ];
    }
}
